import React, { useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { setTableParams } from 'reducers/tableParams';
// components
import { FormattedMessage } from 'react-intl';
import TreatmentProtocolsGroupRow from '../../components/TreatmentProtocolsGroupRow';
import PaginationWrapper from 'components/PaginationWrapper';
import Panel from 'components/Panel';
import Preloader from 'components/Preloader';
import SearchBox from 'components/SearchBox';
import NothingBox from 'components/NothingBox';
// hooks
import useDataTable, { tableParamsSelector } from 'hooks/useDataTable';
// endpoints
import { getTreatmentProtocolsFarmPigGroups } from 'endpoints/treatmentProtocols';
// utils, constants
import { tableNames } from 'utils/constants';
import { toastResponseErrors } from 'utils/responseErrorsHelper';

const TreatmentProtocolsFarmGroups = (
  { farm, params: { farmId }, reqParams, router, setBreadcrumbs, setPageOptions, setTableParams }
) => {
  const {
    data: {
      resources,
      isLoading,
      meta: { total },
    },
    fetchData,
    onPageChange,
    onPerPageChange,
    onSearchChange,
  } = useDataTable((params) => getTreatmentProtocolsFarmPigGroups(farmId, params), {
    tableParams: reqParams,
    setTableParams: (params) => setTableParams(tableNames.treatmentProtocolFarmGroups, params),
  });
  const { page, per_page, search } = reqParams;

  useEffect(() => {
    fetchData({ ...reqParams, page: 1 }).catch(toastResponseErrors);
    setPageOptions({ backLink: '/treatment-protocols-farms' });
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.treatmentProtocolFarms" />,
        path: '/treatment-protocols-farms',
      },
      {
        label: farm?.name || '',
        useLabelAsMobileTitle: true },
    ]);
  }, [farm?.name]);

  const onGroupSelect = ({ farm_id, id }) => {
    router.push(`/treatment-protocols-farms/${farm_id}/groups/${id}/treatment-protocols`);
  };

  return (
    <section className="small-12 column row centered">
      <Panel className="mb-20">
        <Panel.Heading title={<FormattedMessage id="general.pageTitle.groups" />}>
          <SearchBox onChange={onSearchChange} initialValue={search} disableFocus />
        </Panel.Heading>
        <Panel.Body className="pt-20 pb-0 ph-20">
          <Preloader isActive={isLoading} />
          {resources.map((group) => (
            <TreatmentProtocolsGroupRow key={group.id} group={group} onSelect={() => onGroupSelect(group)} />
          ))}
          <NothingBox itemsName="groups" display={!resources.length} isLoading={isLoading}>
            <FormattedMessage tagName="h1" id="component.nothingBox.noGroupsToDisplay" />
          </NothingBox>

          <PaginationWrapper
            currentPage={page}
            perPage={per_page}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
            totalItems={total}
            onlyBottom
          />
        </Panel.Body>
      </Panel>
    </section>
  );
};

TreatmentProtocolsFarmGroups.propTypes = {
  farm: T.object.isRequired,
  params: T.object.isRequired,
  reqParams: T.object.isRequired,
  router: T.object.isRequired,
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  setTableParams: T.func.isRequired,
};

export default connect(
  (state) => ({
    farm: state.profiles.farm.data,
    reqParams: tableParamsSelector(state, tableNames.treatmentProtocolFarmGroups),
  }), {
    setBreadcrumbs,
    setPageOptions,
    setTableParams,
  }
)(TreatmentProtocolsFarmGroups);
